import { FC, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Box, MicrosoftLogo } from '@parspec/pixel';

import InfoCard from '../shared/components/InfoCard';
import { useGetUserProfileInfoQuery } from '../../queries';
import { useDisableSSOSetupMutation, useGetSSOIdentifierMutation } from 'src/features/Authentication/queries';
import { environment } from 'src/environments';
import { someThingWentWrongMsg, SSO_DISABLE_SUCCESS, SSO_ENABLED_SUCCESS } from 'src/features/shared/constants';
import SSOErrorPopup from './SSOErrorPopup';

interface SSOProps {
    handleSnackbarMsg: (msg: string) => void;
}

const SSO: FC<SSOProps> = ({ handleSnackbarMsg }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [showErrorPopup, setShowErrorPopup] = useState(false);

    const showErrorMsg = searchParams.get('error') === 'true';
    const showSuccessMsg = searchParams.get('success') === 'true';
    const isShowPopupParamPresent = searchParams.get('showSSOErrorPopup') === 'true';

    const { data: userData } = useGetUserProfileInfoQuery();

    const { mutateAsync: getSSOIdentifierMutation, isLoading: isGetSSOIdentifierMutationLoading } = useGetSSOIdentifierMutation();
    const { mutateAsync: disableSSO, isLoading: disableSSOLoading } = useDisableSSOSetupMutation();

    const isSSOEnabled = Boolean(userData?.data?.is_sso_enabled);

    const handleToggleErrorPopup = () => setShowErrorPopup(old => !old);

    useEffect(() => {
        if (showErrorMsg) {
            handleSnackbarMsg(someThingWentWrongMsg)
            searchParams.delete('error');
            return setSearchParams(searchParams);
        }
        if (showSuccessMsg) {
            handleSnackbarMsg(SSO_ENABLED_SUCCESS)
            searchParams.delete('success');
            return setSearchParams(searchParams);
        }
        if (isShowPopupParamPresent) {
            handleToggleErrorPopup();
            searchParams.delete('showSSOErrorPopup');
            return setSearchParams(searchParams);
        }
    }, []);

    const onClickEnable = async () => {
        localStorage.setItem('mfaSetupPopup', 'false')
        const response = await getSSOIdentifierMutation();
        const CLIENT_ID = environment.i;
        const REDIRECT_URI = `${environment.R}/login/sso/authorize`;
        const { state, nonce } = response?.data || {};
        const url = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${CLIENT_ID}&response_type=code&redirect_uri=${REDIRECT_URI}&response_mode=query&scope=openid+email+profile&state=${state}&nonce=${nonce}`;
        window.open(url, '_self');
    };

    const onClickDisable = async () => {
        await disableSSO();
        handleSnackbarMsg(SSO_DISABLE_SUCCESS)
    };

    const onClickEnableDisableButton = () => {
        if (isSSOEnabled) {
            return onClickDisable();
        }
        return onClickEnable();
    };

    return (
        <>
            <InfoCard
                title="Single Sign-On (SSO)"
                subTitle="Microsoft"
                helperText="Login to Parspec using your Microsoft account."
                isOptionEnabled={isSSOEnabled}
                onClickEnableDisableButton={onClickEnableDisableButton}
                isLoading={isGetSSOIdentifierMutationLoading || disableSSOLoading}
                isCardDisabled={false}
                icon={<Box height={40} width={40} borderRadius='50%' bgcolor={isSSOEnabled ? 'primary.main' : 'neutral.main'} justifyContent='center' display='flex' alignItems='center'> <MicrosoftLogo fontSize="small" bgColor={isSSOEnabled ? 'primary' : 'neutral'} color={'inherit'} /></Box >}
            />
            {showErrorPopup && <SSOErrorPopup open={showErrorPopup} onClose={handleToggleErrorPopup} />}
        </>
    );
};

export default SSO;
